import React from 'react';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';
import Button, { IconButtonProps } from '@ui/Button';
import Icon from '@ui/Icon';
import { paletteBridge } from 'theme/palette';
import Tooltip from 'components/Tooltip';
import { alphaWhite } from '../../../util/cssUtil';

interface CommentIndicatorButtonProps
  extends Omit<IconButtonProps, 'children'> {
  /**
   * Total number of comments
   */
  commentsTotal: number;
  /**
   * Total number of unread comments
   */
  unreadCommentsTotal: number;
  /**
   * Renders UI styling if true
   * i.e. If right side rail is open, displays a styling to indicate that as well
   */
  open: boolean;
  tooltipPlacement?: 'bottom' | 'top';
  size?: 'small' | 'default';
}

enum CommentIndicatorState {
  Default,
  Unread,
  ReadAll,
}

const CommentIndicatorButton = React.forwardRef<
  HTMLButtonElement,
  CommentIndicatorButtonProps
>((props, ref) => {
  const {
    disabled,
    open,
    commentsTotal,
    unreadCommentsTotal,
    tooltipPlacement = 'bottom',
    size = 'default',
    ...rest
  } = props;
  const { classes, cx } = useStyles();
  const currentState = React.useMemo(() => {
    if (commentsTotal && !unreadCommentsTotal)
      return CommentIndicatorState.ReadAll;
    if (unreadCommentsTotal) return CommentIndicatorState.Unread;
    return CommentIndicatorState.Default;
  }, [commentsTotal, unreadCommentsTotal]);

  return (
    <>
      <Tooltip text="Comment" placement={tooltipPlacement}>
        <span>
          <Button
            {...rest}
            ref={ref}
            variant="icon"
            disabled={disabled}
            className={cx(
              classes.iconButton,
              { [classes.open]: open, [classes.disabled]: disabled },
              size !== 'default' && classes[size]
            )}
          >
            <>
              {currentState === CommentIndicatorState.Unread && (
                <div className={classes.indicator} />
              )}
              {currentState === CommentIndicatorState.ReadAll && (
                <div className={classes.counter}>
                  {commentsTotal > 10 ? `10+` : commentsTotal}
                </div>
              )}
              <Icon type="messageSquare02" />
            </>
          </Button>
        </span>
      </Tooltip>
      {commentsTotal > 10 && <div style={{ width: 4 }} />}
    </>
  );
});

const useStyles = makeStyles()(theme => ({
  iconButton: {
    width: 'fit-content',
    borderRadius: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&&.Mui-focusVisible': {
      outline: `2px solid ${paletteBridge.greens.lime[100]}`,
      outlineOffset: 4,
      position: 'relative',
      zIndex: 1,
    },
  },
  small: {
    width: '24px',
    height: '24px',
  },
  open: {
    backgroundColor: alphaWhite(0.12),
  },
  disabled: {
    color: palette.gray_500,
  },
  indicator: {
    position: 'absolute',
    height: 8,
    width: 8,
    backgroundColor: palette.azure_300,
    borderRadius: '100%',
    outline: `1px solid ${theme.palette.background.paper}`,
    top: 2,
    right: 2,
  },
  counter: {
    position: 'absolute',
    top: -1,
    left: theme.spacing(2.125),
    backgroundColor: palette.gray_1000,
    fontFamily: 'assistant',
    fontWeight: 'bold',
    fontSize: 10,
    padding: theme.spacing(0, 0.75, 0.125, 0.75),
    borderRadius: '100px',
  },
}));

export default CommentIndicatorButton;
